import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './Home.css';

const Home = () => {
  const services = [
    {
      icon: 'fas fa-drafting-compass',
      title: 'Industrial Design',
      description: 'Kitchen & Laundry Equipment Design',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      icon: 'fas fa-blueprint',
      title: 'MEP Executive Drawings',
      description: 'Mechanical, Electrical & Plumbing Plans',
      image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      icon: 'fas fa-chalkboard-teacher',
      title: 'Training Sessions',
      description: 'Professional Operator Training',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      icon: 'fas fa-tools',
      title: 'Maintenance & Repair',
      description: 'Complete Equipment Servicing',
      image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }
  ];

  const projects = [
    {
      title: 'Modern Kitchen Design',
      category: 'Industrial Design',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
    },
    {
      title: 'MEP System Installation',
      category: 'Engineering',
      image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
    },
    {
      title: 'Equipment Training Program',
      category: 'Training',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
    },
    {
      title: 'Facility Maintenance',
      category: 'Maintenance',
      image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
    }
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          <div className="hero-slide active">
            <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Modern Office" />
          </div>
        </div>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="container-wide">
            <div className="container">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                className="hero-text"
              >
              <h1 className="hero-title">
                <span className="gradient-text text-shimmer">EFFICIENT GROUP</span>
                <br />
                Facilities Management Excellence
              </h1>
              <p className="hero-subtitle">
                Leading provider of comprehensive facilities management solutions, 
                industrial design, MEP services, training programs, and maintenance excellence.
              </p>
              <div className="hero-buttons">
                <Link to="/services" className="btn btn-primary morph-button hover-bounce">
                  Explore Services
                  <i className="fas fa-arrow-right"></i>
                </Link>
                <Link to="/contact" className="btn btn-secondary morph-button hover-shake">
                  Get Quote
                  <i className="fas fa-phone"></i>
                </Link>
              </div>
              </motion.div>
            </div>
          </div>
        </div>
        <div className="hero-scroll">
          <div className="scroll-indicator">
            <span>Scroll Down</span>
            <div className="scroll-arrow"></div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-preview section">
        <div className="container-wide">
          <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Core Services</h2>
            <p className="section-subtitle">
              Comprehensive facilities management solutions tailored to your needs
            </p>
          </motion.div>
          
          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="service-card card-hover-lift hover-tilt ripple-effect"
                whileHover={{ y: -15, rotateY: 5 }}
              >
                <div className="service-image">
                  <img src={service.image} alt={service.title} />
                  <div className="service-overlay">
                    <i className={service.icon}></i>
                  </div>
                </div>
                <div className="service-content">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <Link to={`/services/${index === 0 ? 'industrial-design' : index === 1 ? 'mep-services' : index === 2 ? 'training' : 'after-sale'}`} className="service-link">
                    Learn More <i className="fas fa-arrow-right"></i>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="about-preview section parallax-bg">
        <div className="parallax-overlay"></div>
        <div className="container">
          <div className="about-content">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="about-text"
            >
              <h2 className="section-title">Why Choose Efficient Group?</h2>
              <p className="section-subtitle">
                With years of experience in facilities management, we deliver excellence 
                in every project we undertake.
              </p>
              <div className="features-list">
                <div className="feature-item">
                  <i className="fas fa-check-circle"></i>
                  <span>Expert Industrial Design Solutions</span>
                </div>
                <div className="feature-item">
                  <i className="fas fa-check-circle"></i>
                  <span>Comprehensive MEP Services</span>
                </div>
                <div className="feature-item">
                  <i className="fas fa-check-circle"></i>
                  <span>Professional Training Programs</span>
                </div>
                <div className="feature-item">
                  <i className="fas fa-check-circle"></i>
                  <span>24/7 Maintenance Support</span>
                </div>
              </div>
              <Link to="/about" className="btn btn-primary">
                Learn More About Us
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="about-images"
            >
              <div className="image-grid">
                <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" alt="Team Work" />
                <img src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" alt="Equipment" />
                <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" alt="Training" />
                <img src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" alt="Kitchen Design" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Projects Preview */}
      <section className="projects-preview section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Recent Projects</h2>
            <p className="section-subtitle">
              Showcasing our latest work and successful implementations
            </p>
          </motion.div>
          
          <div className="projects-grid">
            {projects.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="project-card hover-zoom-rotate gradient-border"
                whileHover={{ y: -10, scale: 1.05 }}
              >
                <div className="project-image">
                  <img src={project.image} alt={project.title} />
                  <div className="project-overlay">
                    <div className="project-info">
                      <span className="project-category">{project.category}</span>
                      <h3 className="project-title">{project.title}</h3>
                      <Link to="/projects" className="project-link">
                        <i className="fas fa-external-link-alt"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="projects-cta">
            <Link to="/projects" className="btn btn-primary">
              View All Projects
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-background">
          <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Contact Us" />
        </div>
        <div className="cta-overlay"></div>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="cta-content"
          >
            <h2>Ready to Transform Your Facility?</h2>
            <p>Get in touch with our experts for a comprehensive consultation</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                Get Started Today
              </Link>
              <a href="tel:+1234567890" className="btn btn-secondary">
                Call Now: +1 (234) 567-890
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;